import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Skeleton } from "../components/ui/skeleton";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const kategori = params.get("kategori") || "";
  const etiket = params.get("etiket") || "";
  const siralama = params.get("siralama") || "";
  const sayfa = Number(params.get("sayfa") || 1);
  const minFiyat = params.get("min") || "";
  const maxFiyat = params.get("max") || "";
  const marka = params.get("marka") || "";
  const tur = params.get("tur") || "";
  const kullanim = params.get("kullanim") || "";
  const stokta = params.get("stokta") === "1";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (kategori) qs.set("kategori", kategori);
    if (etiket) qs.set("etiket", etiket);
    if (siralama) qs.set("siralama", siralama);
    if (minFiyat) qs.set("min_fiyat", minFiyat);
    if (maxFiyat) qs.set("max_fiyat", maxFiyat);
    if (marka) qs.set("marka", marka);
    if (tur) qs.set("tur", tur);
    if (kullanim) qs.set("kullanim", kullanim);
    if (stokta) qs.set("stok", "true");
    qs.set("sayfa", sayfa);
    api.get(`/products?${qs.toString()}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [q, kategori, etiket, siralama, sayfa, minFiyat, maxFiyat, marka, stokta, tur, kullanim]);

  const set = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "sayfa") next.delete("sayfa");
    setParams(next);
  };

  const title = kategori ? categories.find((c) => c.slug === kategori)?.name : q ? `"${q}" için sonuçlar` : etiket === "cok-satan" ? "Çok Satanlar" : etiket === "yeni" ? "Yeni Ürünler" : etiket === "kampanya" ? "Kampanyalı Ürünler" : "Tüm Ürünler";

  return (
    <div data-testid="products-page" className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="font-serif text-3xl sm:text-4xl text-forest">{title}</h1>
      <p className="text-stone-500 text-sm mt-2">{data ? `${data.total} ürün bulundu` : "Yükleniyor..."}</p>

      <div className="flex flex-col lg:flex-row gap-8 mt-8">
        <aside className="lg:w-64 shrink-0 space-y-6">
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5">
            <h3 className="font-semibold text-stone-900 text-sm uppercase tracking-wider mb-3">Kategoriler</h3>
            <div className="space-y-1">
              <button data-testid="filter-category-all" onClick={() => set("kategori", "")} className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!kategori ? "bg-forest text-white" : "hover:bg-cream-dark text-stone-700"}`}>Tümü</button>
              {categories.map((c) => (
                <button key={c.category_id} data-testid={`filter-category-${c.slug}`} onClick={() => set("kategori", c.slug)} className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${kategori === c.slug ? "bg-forest text-white" : "hover:bg-cream-dark text-stone-700"}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5">
            <h3 className="font-semibold text-stone-900 text-sm uppercase tracking-wider mb-3">Fiyat Aralığı</h3>
            <div className="flex gap-2">
              <input data-testid="filter-min-price" type="number" placeholder="Min" defaultValue={minFiyat} onBlur={(e) => set("min", e.target.value)} className="w-full h-10 px-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" />
              <input data-testid="filter-max-price" type="number" placeholder="Max" defaultValue={maxFiyat} onBlur={(e) => set("max", e.target.value)} className="w-full h-10 px-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" />
            </div>
          </div>
          {data?.brands?.filter(Boolean).length > 1 && (
            <div className="bg-white rounded-2xl border border-stone-200/80 p-5">
              <h3 className="font-semibold text-stone-900 text-sm uppercase tracking-wider mb-3">Marka</h3>
              <div className="space-y-1">
                <button data-testid="filter-brand-all" onClick={() => set("marka", "")} className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!marka ? "bg-forest text-white" : "hover:bg-cream-dark text-stone-700"}`}>Tümü</button>
                {data.brands.filter(Boolean).map((b) => (
                  <button key={b} data-testid={`filter-brand-${b}`} onClick={() => set("marka", b)} className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${marka === b ? "bg-forest text-white" : "hover:bg-cream-dark text-stone-700"}`}>{b}</button>
                ))}
              </div>
            </div>
          )}
          {data?.types?.length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200/80 p-5">
              <h3 className="font-semibold text-stone-900 text-sm uppercase tracking-wider mb-3">Ürün Türü</h3>
              <select data-testid="filter-type" value={tur} onChange={(e) => set("tur", e.target.value)} className="w-full h-10 px-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 bg-white">
                <option value="">Tümü</option>
                {data.types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          {data?.usages?.length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200/80 p-5">
              <h3 className="font-semibold text-stone-900 text-sm uppercase tracking-wider mb-3">Kullanım Alanı</h3>
              <select data-testid="filter-usage" value={kullanim} onChange={(e) => set("kullanim", e.target.value)} className="w-full h-10 px-3 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 bg-white">
                <option value="">Tümü</option>
                {data.usages.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5 space-y-3">
            <h3 className="font-semibold text-stone-900 text-sm uppercase tracking-wider">Diğer</h3>
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input data-testid="filter-in-stock" type="checkbox" checked={stokta} onChange={(e) => set("stokta", e.target.checked ? "1" : "")} className="w-4 h-4 accent-forest" /> Sadece stokta olanlar
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input data-testid="filter-discount" type="checkbox" checked={etiket === "kampanya"} onChange={(e) => set("etiket", e.target.checked ? "kampanya" : "")} className="w-4 h-4 accent-forest" /> İndirimli ürünler
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input data-testid="filter-new" type="checkbox" checked={etiket === "yeni"} onChange={(e) => set("etiket", e.target.checked ? "yeni" : "")} className="w-4 h-4 accent-forest" /> Yeni ürünler
            </label>
          </div>
        </aside>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-stone-500 hidden sm:block">{data?.total ?? 0} ürün</p>
            <select
              data-testid="sort-select"
              value={siralama}
              onChange={(e) => set("siralama", e.target.value)}
              className="h-10 px-3 rounded-lg border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30"
            >
              <option value="">Önerilen Sıralama</option>
              <option value="cok-satan">Çok Satanlar</option>
              <option value="fiyat-artan">Fiyat: Düşükten Yükseğe</option>
              <option value="fiyat-azalan">Fiyat: Yüksekten Düşüğe</option>
              <option value="yeni">Yeni Gelenler</option>
              <option value="puan">Puana Göre</option>
            </select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}
            </div>
          ) : data?.items.length ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {data.items.map((p) => <ProductCard key={p.product_id} product={p} />)}
              </div>
              {data.sayfa_sayisi > 1 && (
                <div className="flex justify-center gap-2 mt-10">
                  {Array.from({ length: data.sayfa_sayisi }).map((_, i) => (
                    <button key={i} data-testid={`page-${i + 1}`} onClick={() => set("sayfa", String(i + 1))} className={`w-10 h-10 rounded-xl text-sm font-semibold transition-colors ${sayfa === i + 1 ? "bg-forest text-white" : "bg-white border border-stone-300 text-stone-700 hover:bg-cream-dark"}`}>{i + 1}</button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div data-testid="no-products-message" className="text-center py-20 bg-white rounded-2xl border border-stone-200/80">
              <p className="font-serif text-xl text-stone-700">Ürün bulunamadı</p>
              <p className="text-stone-500 text-sm mt-2">Farklı bir arama veya filtre deneyin.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
